import { useCallback, useEffect, useRef, useState } from "react";
import { RequireRole } from "../../auth/RequireRole";
import { describeError } from "../../api/client";
import type { AdminContactImportResult } from "../../api/types";
import { Combobox } from "../../components/Combobox";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { Modal } from "../../components/Modal";
import { ModalCloseButton } from "../../components/ModalCloseButton";
import { useQuery } from "../../state/useQuery";
import { getShowOptions, importContacts } from "./api";
import {
  autoMapColumns,
  buildImportRows,
  decodeCsvText,
  FIELD_LABELS,
  headersMatch,
  parseCsv,
  remapToNewHeaders,
  REQUIRED_TARGET_FIELDS,
  TARGET_FIELDS,
  type ColumnMapping,
  type ParsedCsv,
} from "./csv";
import { ShowSelector } from "./ShowSelector";
import { parseXlsx } from "./xlsx";

// Design handoff v2 (líneas 719/1868), kept for §7.3's dropzone/trigger icon
// -- purely decorative (aria-hidden), same outline-icon style (round caps/
// joins, no fill) as every other v2/v3 icon.
function UploadIcon({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Prototype upload glyph (Panel Rediseño.dc.html, MODAL: IMPORTAR). */}
      <path d="M12 15V4M7.5 8.5 12 4l4.5 4.5M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
    </svg>
  );
}

/** §7.3 step 4's green check glyph. */
function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={26}
      height={26}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12l5 5 9-10" />
    </svg>
  );
}

/**
 * Codex PR #386 review (comment 4373699169): an interrupted import used the
 * SAME green check as a normal completion, telling the operator the import
 * succeeded while the red interruption panel right below it said otherwise.
 * This amber warning triangle (same icon vocabulary as every other v2/v3
 * icon) replaces the check glyph whenever `result.interrumpido` is true.
 */
function WarningIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={26}
      height={26}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 9v4" />
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 17h.01" />
    </svg>
  );
}

// UX audit 2026-09-15, finding #9 MEDIUM: the fix proposal explicitly asked
// for a downloadable CSV template, not just a list of required column names.
// Built from the same FIELD_LABELS/TARGET_FIELDS the mapping already uses,
// so the template header row can never drift out of sync with what the
// mapping step actually expects.
const CSV_TEMPLATE_HREF = `data:text/csv;charset=utf-8,${encodeURIComponent(
  `${TARGET_FIELDS.map((field) => FIELD_LABELS[field]).join(",")}\n`,
)}`;

/** "Email, Nombre y Empresa" -- §7.3 step 2's required-columns sentence, built from the REAL required fields so it can never say something the validation below doesn't enforce. */
function formatRequiredFieldsSentence(): string {
  const labels = REQUIRED_TARGET_FIELDS.map((field) => FIELD_LABELS[field]);
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} y ${labels[labels.length - 1]}`;
}

const EMPTY_MAPPING: ColumnMapping = {
  email: "",
  nombre: "",
  empresa: "",
  cargo: "",
  nivelJerarquico: "",
  industria: "",
  zonaHoraria: "",
  estado: "",
};

type Step = 1 | 2 | 3 | 4;

const STEP_LABELS: readonly { step: Step; label: string }[] = [
  { step: 1, label: "Show" },
  { step: 2, label: "Archivo" },
  { step: 3, label: "Revisión" },
  { step: 4, label: "Resultado" },
];

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "done"; result: AdminContactImportResult; sourceRows?: number[] }
  | { status: "error"; message: string };

/**
 * RDD (review-reliability, WARNING, R3-001): a distinct error type (rather
 * than string-matching a message) lets handleFileChange's catch block tell
 * "file parsed but had zero headers" apart from every other parse failure
 * (corrupt XLSX, XLSX.read throwing, etc.), so the operator sees a message
 * specific to what actually happened instead of always the generic one.
 */
class EmptyFileError extends Error {
  constructor() {
    super("El archivo esta vacio.");
    this.name = "EmptyFileError";
  }
}

interface ContactImportPanelProps {
  /** Called once after a successful (even if partial/interrumpido) submit — lets the caller refresh the contacts list/counts. */
  onImported?: (result: AdminContactImportResult) => void;
  /** §7.3 step 4 "Ver contactos": closes the panel and filters the list by the Show this import targeted. */
  onViewContacts?: (showId: string) => void;
}

/**
 * REDISENO-PANEL.md §7.3 — a 4-step modal (Show · Archivo · Revisión ·
 * Resultado, `<ol aria-current="step">`) replacing the former single-scroll
 * panel. Parsing/mapping/submit logic (CP1252/CSV decoding, auto-mapping,
 * `AdminContactImportValidator`-mirrored required fields) is unchanged —
 * only the step-by-step shell and the new Resultado step (figures + rejects
 * list, rendered in the modal itself instead of a dismissible toast) are new.
 * Hidden entirely for the Consulta role (client-side gate; the server
 * enforces this independently via `AdminRouteTable`).
 */
export function ContactImportPanel({ onImported, onViewContacts }: ContactImportPanelProps) {
  return (
    <RequireRole role="Operador">
      <ContactImportPanelContent onImported={onImported} onViewContacts={onViewContacts} />
    </RequireRole>
  );
}

function ContactImportPanelContent({ onImported, onViewContacts }: ContactImportPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<Step>(1);
  const [csv, setCsv] = useState<ParsedCsv | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>(EMPTY_MAPPING);
  const [showId, setShowId] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>({ status: "idle" });

  // Codex review (PR #263, P1): FileReader completions can resolve out of
  // order when multiple files are selected in quick succession (e.g. a
  // slower file selected first, then a faster one selected before the first
  // finishes). Without this, a slower/abandoned read completing AFTER a
  // newer selection would silently clobber the newer selection's csv and/or
  // mapping state with stale data. Every handleFileChange call claims the
  // next id; a completion only applies its result if it's still the latest.
  const latestFileRequestIdRef = useRef(0);

  // §7.3 "Resultado" needs the Show's NAME ("N contactos importados en
  // {show}") -- same "show-options" query key `ShowSelector`/`ContactsScreen`
  // already use, so this shares their cache instead of firing a second fetch.
  const { data: showOptions } = useQuery({ key: ["show-options"], queryFn: () => getShowOptions() });
  const selectedShowName = showOptions?.find((option) => option.showId === showId)?.nombre ?? "";

  function resetWizard() {
    setStep(1);
    setCsv(null);
    setFileName(null);
    setMapping(EMPTY_MAPPING);
    setShowId("");
    setSubmitState({ status: "idle" });
    // Codex PR #386 review: without this, a file read started before the
    // panel was closed (and thus abandoned) could still complete AFTER this
    // reset and write its stale csv/fileName/mapping into the just-reset
    // wizard -- invalidating every requestId already in flight, same as the
    // out-of-order-selection guard in handleFileChange above.
    latestFileRequestIdRef.current += 1;
  }

  function openPanel() {
    resetWizard();
    setIsOpen(true);
  }

  // `Modal` binds its Escape/Tab handlers ONCE at mount (same documented
  // convention `ConfirmDialog` already uses) -- the `onClose` closure it
  // captures is whichever one was passed on that first render (always
  // `submitState.status === "idle"`, right after `resetWizard()`), so
  // `requestClose` must read "is a submit in flight" from a REF, never
  // from `submitState` directly, or a later Escape press would act on the
  // STALE idle value captured when the panel first opened (MUST-apply
  // lesson: Escape must never close a modal while it is submitting).
  const submittingRef = useRef(false);
  submittingRef.current = submitState.status === "submitting";

  function requestClose() {
    if (submittingRef.current) {
      return;
    }
    setIsOpen(false);
  }

  // Codex PR #386 review: the "Siguiente"/"Atrás" buttons that drive step
  // changes live in the footer, outside each step's own body content, and
  // the elements under them change completely on every step -- without
  // this, focus either stayed on a button whose meaning just changed (still
  // "Siguiente", but now the NEXT step's) or was silently dropped onto
  // <body> when the previously-focused control unmounted. Each step body
  // below renders this SAME heading ref, so a keyboard/screen-reader user
  // always lands on the new step's own heading.
  // RDD (review-readability, SUGGESTION, R3-004): keyed on `step` alone, this
  // effect never re-ran when the panel reopened back on the SAME step it had
  // last time (resetWizard's setStep(1) is a same-value no-op when the
  // previous session already ended on step 1) -- `isOpen` makes every fresh
  // open move focus to the step heading too, not just a later step change.
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (isOpen) {
      stepHeadingRef.current?.focus();
    }
  }, [step, isOpen]);

  // Codex PR #386 review (comment 4373699180): the copy told operators they
  // could drag a CSV/XLSX onto the dropzone, but nothing handled drag/drop --
  // the real `<input type="file">` the label wraps is reduced to 1x1px by
  // modal-v2.css, and dropping onto the visible label never dispatches
  // `change` on it. `processFile` is the exact same parse/decode/auto-map
  // pipeline `handleFileChange`'s `onload` used to run inline, now shared so
  // a dropped file (no `<input>` involved at all) gets identical treatment --
  // `resetInput` is the only input-specific side effect (clearing the real
  // `<input>`'s value so re-selecting the same path still fires `change`),
  // a no-op for a drop, which never touches that input.
  const processFile = useCallback(
    (file: File, resetInput?: () => void) => {
      // Issue #258, hallazgo #5: encabezados del archivo ANTERIOR (si habia
      // uno), para decidir mas abajo si el archivo nuevo es "el mismo de
      // nuevo" -- en ese caso se conserva el mapeo manual ya hecho en vez de
      // recalcular auto-mapeo desde cero.
      const previousHeaders = csv?.headers ?? null;
      const requestId = ++latestFileRequestIdRef.current;

      // FileReader instead of Blob.text()/Blob.arrayBuffer(): jsdom's Blob
      // methods can hang indefinitely when reading a File constructed via
      // @testing-library/user-event's upload() helper, timing out every
      // findByLabelText wait in the test suite. FileReader's read* methods are
      // the long-established, reliably-supported API for this in both real
      // browsers and jsdom.
      //
      // epic #236 tarea #238 (work unit 3): un .xlsx es binario — leerlo como
      // texto (readAsText) corrompe su contenido. La extension del archivo
      // decide la rama; cualquier otra cosa (incluido "sin extension
      // reconocida") sigue el camino CSV de siempre, sin cambios de
      // comportamiento para el flujo existente.
      //
      // Bug encontrado en dev (23 sep): el camino CSV usaba readAsText, que
      // decodifica siempre como UTF-8 -- un CSV real del cliente (exportado
      // como "CSV (Comma delimited)" de Excel, cp1252) llegaba con las
      // tildes/enie convertidas en U+FFFD ("Control Log�stico"). Ahora AMBAS
      // ramas leen como ArrayBuffer y el CSV pasa por decodeCsvText (UTF-8
      // estricto, con fallback a windows-1252) antes de parseCsv.
      const isXlsx = file.name.toLowerCase().endsWith(".xlsx");
      const reader = new FileReader();
      reader.onload = () => {
        // Codex review (PR #263, P1): this completion belongs to an earlier,
        // abandoned selection -- a newer file was selected before this one
        // finished reading. Applying it now would overwrite state a more
        // recent (and thus more intended) selection already set correctly.
        if (requestId !== latestFileRequestIdRef.current) {
          resetInput?.();
          return;
        }

        // Correccion RDD (review-reliability, CRITICAL, R3-xlsx-read-unguarded):
        // XLSX.read lanza si el archivo esta corrupto o simplemente fue
        // renombrado a .xlsx sin serlo — sin este try/catch esa excepcion
        // escapaba de onload sin actualizar ningun estado, dejando el panel
        // congelado en lo que hubiera antes, sin ningun mensaje para el
        // operador. parseCsv nunca lanza (solo trocea texto), pero el catch
        // cubre ambas ramas por uniformidad.
        try {
          const parsed = isXlsx
            ? parseXlsx(reader.result as ArrayBuffer)
            : parseCsv(decodeCsvText(reader.result as ArrayBuffer));
          // Codex review (PR #263, P2): a genuinely empty (or all-blank) file
          // parses successfully into {headers:[], rows:[]} -- without this
          // check, the mapping table (which only renders when headers exist)
          // never appears and no error shows either, so the panel looks
          // EXACTLY like nothing was selected at all.
          if (parsed.headers.length === 0) {
            throw new EmptyFileError();
          }
          setCsv(parsed);
          setFileName(file.name);
          // Issue #258 (hallazgo #1): re-uploading the app's own template (or
          // any file whose headers exactly match a target field's label) used
          // to still force 5+ manual dropdown picks -- auto-map first, then
          // let the operator correct any field autoMapColumns couldn't
          // confidently match.
          // Issue #258 (hallazgo #5): but if this file's headers are the same
          // SET as the previous one's (RDD R3-004: ignoring order and
          // case/whitespace), it's the same export re-uploaded (e.g. after
          // fixing a typo, or reordered/recased) -- keep whatever the operator
          // already mapped instead of throwing it away, translated to the new
          // file's exact header spelling so buildImportRows' case-sensitive
          // lookup still finds each column.
          setMapping((previousMapping) =>
            previousHeaders !== null && headersMatch(previousHeaders, parsed.headers)
              ? remapToNewHeaders(previousMapping, parsed.headers)
              : autoMapColumns(parsed.headers),
          );
          setSubmitState({ status: "idle" });
        } catch (error) {
          setCsv(null);
          setFileName(null);
          // RDD (review-reliability, WARNING, R3-001): this catch-all used to
          // always overwrite EmptyFileError's specific message with the
          // generic parse-failure text, making the "archivo vacio" signal
          // unreachable in the UI -- only a corrupted/invalid file (an
          // XLSX.read or truly malformed CSV throw) gets the generic message
          // now; a genuinely empty file gets its own distinct message.
          setSubmitState({
            status: "error",
            message:
              error instanceof EmptyFileError
                ? error.message
                : "No se pudo leer el archivo. Verifique que sea un CSV o XLSX valido.",
          });
        } finally {
          resetInput?.();
        }
      };

      // RDD (review-reliability, WARNING, R3-002): reader.onerror is a
      // genuine FileReader-level read failure (e.g. an OS-level
      // permission/read error) -- a materially different failure mode from a
      // parseCsv/parseXlsx exception, and was never handled at all. Mirrors
      // the same stale-completion guard, error message, and input reset as
      // the success/parse-failure path above.
      reader.onerror = () => {
        if (requestId !== latestFileRequestIdRef.current) {
          resetInput?.();
          return;
        }
        setCsv(null);
        setFileName(null);
        setSubmitState({ status: "error", message: "No se pudo leer el archivo. Verifique que sea un CSV o XLSX valido." });
        resetInput?.();
      };

      reader.readAsArrayBuffer(file);
    },
    [csv],
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }
      // Issue #258, hallazgo #4: capturado ANTES del callback async de
      // FileReader -- un navegador real no dispara `change` de nuevo si el
      // operador re-selecciona el MISMO archivo mientras el input siga
      // mostrando esa misma ruta. Reseteado en el callback (exito o error) mas
      // abajo para que reintentar el mismo archivo funcione siempre.
      const inputElement = event.target;
      processFile(file, () => {
        inputElement.value = "";
      });
    },
    [processFile],
  );

  // Codex PR #386 review (comment 4373699180): real drag/drop support for the
  // dropzone, in addition to (never instead of) the native `<input
  // type="file">` the label still wraps -- that input stays focusable/
  // clickable exactly as before, unaffected by these handlers.
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDraggingOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      setIsDraggingOver(false);
      const file = event.dataTransfer.files?.[0];
      if (!file) {
        return;
      }
      // Native advisory: the real `<input>`'s `accept` attribute
      // (".csv,text/csv,.xlsx,application/vnd...") only constrains the OS
      // file picker -- it never applies to a drop. Without this check, every
      // non-.xlsx dropped name (regardless of its real extension) fell
      // through to the CSV decode/parse path, so an unrelated file (a PDF,
      // an image, ...) could parse into garbage headers instead of showing
      // a "not a valid CSV/XLSX" error. Same two extensions the input's own
      // `accept` lists, checked case-insensitively.
      const lowerName = file.name.toLowerCase();
      if (!lowerName.endsWith(".csv") && !lowerName.endsWith(".xlsx")) {
        setCsv(null);
        setFileName(null);
        setSubmitState({ status: "error", message: "Solo se admiten archivos CSV o XLSX." });
        return;
      }
      processFile(file);
    },
    [processFile],
  );

  const handleMappingChange = useCallback((field: (typeof TARGET_FIELDS)[number], header: string) => {
    setMapping((prev) => ({ ...prev, [field]: header }));
  }, []);

  const missingMappedLabels = REQUIRED_TARGET_FIELDS.filter((field) => mapping[field] === "").map(
    (field) => FIELD_LABELS[field],
  );
  const canSubmit = csv !== null && csv.rows.length > 0 && missingMappedLabels.length === 0;

  const handleSubmit = useCallback(async () => {
    if (!csv || !showId || !canSubmit) {
      return;
    }

    setSubmitState({ status: "submitting" });
    try {
      const rows = buildImportRows(csv.headers, csv.rows, mapping);
      const result = await importContacts(showId, rows);
      setSubmitState({ status: "done", result, sourceRows: csv.sourceRows });
      setStep(4);
      onImported?.(result);
    } catch (error) {
      // Issue #258, hallazgo #8: antes se mostraba el MISMO mensaje generico
      // para cualquier falla (red, 4xx de validacion, 413 archivo demasiado
      // grande) -- describeError ya existe (usado por ShowSelector) y
      // devuelve el mensaje REAL del servidor para un ApiError, cayendo al
      // generico solo para fallas sin nada mas especifico que decir (red).
      setSubmitState({ status: "error", message: describeError(error, "No se pudo completar la importacion.") });
    }
  }, [csv, canSubmit, mapping, onImported, showId]);

  const mappedChips = TARGET_FIELDS.filter((field) => mapping[field] !== "");

  return (
    <>
      <button type="button" className="v2-u-target-min v2-contact-import__trigger" onClick={openPanel}>
        <UploadIcon size={16} />
        Importar contactos
      </button>

      {isOpen && (
        <Modal ariaLabel="Importar contactos" onClose={requestClose}>
          <div className="v2-modal-v2 v2-modal-v2--w640 v2-contact-import">
            <header className="v2-modal-v2__header v2-modal-v2__header--stacked">
              <div className="v2-contact-import__header-title">
                <h2>Importar contactos</h2>
                <ModalCloseButton onClose={requestClose} disabled={submitState.status === "submitting"} />
              </div>
              <ol className="v2-contact-import__steps" aria-label="Pasos">
                {STEP_LABELS.map(({ step: stepNumber, label }) => (
                  <li
                    key={stepNumber}
                    aria-current={step === stepNumber ? "step" : undefined}
                    className={
                      step > stepNumber
                        ? "v2-contact-import__step v2-contact-import__step--done"
                        : step === stepNumber
                          ? "v2-contact-import__step v2-contact-import__step--active"
                          : "v2-contact-import__step"
                    }
                  >
                    <span className="v2-contact-import__step-bar" aria-hidden="true" />
                    <span className="v2-contact-import__step-label">{label}</span>
                  </li>
                ))}
              </ol>
            </header>

            <div className="v2-modal-v2__body">
              {/* Visually hidden (the visible step indicator in the header already
                  shows the current step) -- its only job is being a stable,
                  always-present focus target for the effect above. */}
              <h3 ref={stepHeadingRef} tabIndex={-1} className="v2-u-visually-hidden">
                Paso {step}: {STEP_LABELS.find((item) => item.step === step)?.label}
              </h3>
              {step === 1 && (
                <>
                  <p className="v2-modal-v2__hint--lead">
                    Todos los contactos del archivo quedarán asociados al Show que elijas.
                  </p>
                  {/* feat/show-create-on-import: allowCreate habilita crear un
                      Show al vuelo (get-or-create por nombre) sin salir del
                      import. renderAs="radio": §7.3 "opciones de radio
                      grandes". */}
                  <ShowSelector
                    id="contact-import-show"
                    value={showId}
                    onChange={setShowId}
                    disabled={submitState.status === "submitting"}
                    allowCreate
                    renderAs="radio"
                  />
                </>
              )}

              {step === 2 && (
                <>
                  <label
                    htmlFor="contact-import-file"
                    className={
                      isDraggingOver
                        ? "v2-dropzone v2-dropzone--stacked v2-dropzone--dragover"
                        : "v2-dropzone v2-dropzone--stacked"
                    }
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <UploadIcon size={28} />
                    <span className="v2-u-visually-hidden">Archivo CSV o XLSX</span>
                    <span className="v2-dropzone__title" aria-hidden="true">
                      {fileName ? `${fileName} · ${csv?.rows.length ?? 0} filas` : "Elige un archivo"}
                    </span>
                    <span className="v2-dropzone__meta" aria-hidden="true">
                      Arrastra un CSV o XLSX, o haz clic para elegirlo
                    </span>
                    <input
                      id="contact-import-file"
                      type="file"
                      accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="v2-modal-v2__hint v2-contact-import__note">
                    Necesitamos al menos {formatRequiredFieldsSentence()}; los nombres de columna pueden variar.{" "}
                    <a
                      className="v2-contact-import__template-link"
                      href={CSV_TEMPLATE_HREF}
                      download="plantilla-contactos.csv"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width={14}
                        height={14}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 4v11m0 0l-4-4m4 4l4-4M5 20h14" />
                      </svg>
                      Descargar plantilla CSV
                    </a>
                  </p>
                  {submitState.status === "error" && <p role="alert">{submitState.message}</p>}
                </>
              )}

              {step === 3 && csv && (
                <>
                  <p className="v2-contact-import__summary-lead">
                    Encontramos <strong>{csv.rows.length} filas</strong> en <strong>{fileName}</strong>. Así las vamos a leer:
                  </p>
                  <ul className="v2-contact-import__mapping-chips" aria-label="Mapeo de columnas">
                    {mappedChips.map((field) => (
                      <li key={field}>
                        <code>{mapping[field]}</code>
                        <span className="v2-contact-import__mapping-arrow" aria-hidden="true">
                          →
                        </span>
                        <strong>{FIELD_LABELS[field]}</strong>
                      </li>
                    ))}
                  </ul>

                  {/* §7.3: "vista previa de 3–5 filas". */}
                  <div className="v2-contact-import__preview">
                    <table aria-label="Vista previa de datos">
                      <thead>
                        <tr>
                          {csv.headers.map((header, index) => (
                            <th key={index}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csv.rows.slice(0, 5).map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {csv.headers.map((_header, columnIndex) => (
                              <td key={columnIndex}>{row[columnIndex] ?? ""}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* User follow-up 2026-09-22: manual correction stays
                      available (collapsed by default) for the rare case
                      autoMapColumns couldn't confidently match every
                      column -- the chips above already show the current
                      (correct, in the common case) mapping. */}
                  <details className="v2-contact-import__mapping-details">
                    <summary>Ajustar mapeo manualmente</summary>
                    <div className="v2-u-table-scroll">
                      <table>
                        <caption>Mapeo de columnas</caption>
                        <tbody>
                          {TARGET_FIELDS.map((field) => {
                            // Issue #258, hallazgo #3: sin esto, dos campos
                            // podian mapearse a la misma columna sin aviso.
                            const usedByOtherField = new Set(
                              TARGET_FIELDS.filter((other) => other !== field && mapping[other] !== "").map(
                                (other) => mapping[other],
                              ),
                            );
                            const availableHeaders = csv.headers.filter(
                              (header) => header === mapping[field] || !usedByOtherField.has(header),
                            );

                            return (
                              <tr key={field}>
                                <th scope="row">
                                  <label htmlFor={`mapping-${field}`}>
                                    {FIELD_LABELS[field]}
                                    {REQUIRED_TARGET_FIELDS.includes(field) ? " *" : ""}
                                  </label>
                                </th>
                                <td>
                                  <Combobox
                                    id={`mapping-${field}`}
                                    searchable={false}
                                    value={mapping[field]}
                                    onChange={(next) => handleMappingChange(field, next)}
                                    options={[
                                      { value: "", label: "Sin mapear" },
                                      ...availableHeaders.map((header) => ({ value: header, label: header })),
                                    ]}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </details>

                  {submitState.status === "error" && <p role="alert">{submitState.message}</p>}
                </>
              )}

              {step === 4 && submitState.status === "done" && (
                <ImportResultStep
                  result={submitState.result}
                  showName={selectedShowName}
                  sourceRows={submitState.sourceRows}
                />
              )}
            </div>

            <footer className="v2-modal-v2__footer">
              {step === 3 && missingMappedLabels.length > 0 ? (
                <p className="v2-modal-v2__footer-note">Para habilitar &quot;Importar&quot; falta mapear {missingMappedLabels.join(", ")}.</p>
              ) : (
                step < 4 && <p className="v2-modal-v2__footer-note">Paso {step} de 3</p>
              )}
              {step < 4 && (
                <button
                  type="button"
                  className="v2-u-target-min v2-btn-secondary"
                  onClick={step === 1 ? requestClose : () => setStep((current) => (current - 1) as Step)}
                  disabled={submitState.status === "submitting"}
                >
                  {step === 1 ? "Cancelar" : "Atrás"}
                </button>
              )}
              {step === 1 && (
                <button
                  type="button"
                  className="v2-u-target-min v2-btn-primary"
                  onClick={() => setStep(2)}
                  disabled={showId === ""}
                >
                  Siguiente
                </button>
              )}
              {step === 2 && (
                <button
                  type="button"
                  className="v2-u-target-min v2-btn-primary"
                  onClick={() => setStep(3)}
                  disabled={csv === null || csv.rows.length === 0}
                >
                  Siguiente
                </button>
              )}
              {step === 3 && (
                <button
                  type="button"
                  className="v2-u-target-min v2-btn-primary"
                  onClick={() => void handleSubmit()}
                  disabled={!canSubmit || submitState.status === "submitting"}
                  aria-busy={submitState.status === "submitting"}
                >
                  {submitState.status === "submitting" ? "Importando…" : `Importar ${csv?.rows.length ?? 0} filas`}
                </button>
              )}
              {step === 4 && (
                <button
                  type="button"
                  className="v2-u-target-min v2-btn-primary"
                  onClick={() => {
                    setIsOpen(false);
                    onViewContacts?.(showId);
                  }}
                >
                  Ver contactos
                </button>
              )}
            </footer>
          </div>
        </Modal>
      )}
    </>
  );
}

/** §7.3 step 4: check/warning + "Importación terminada"/"Importación interrumpida", 3 figures, rejects list, and the interrumpido notice when the import stopped mid-way. */
function ImportResultStep({
  result,
  showName,
  sourceRows,
}: {
  result: AdminContactImportResult;
  showName: string;
  /** Source-file row of each submitted record (see `ParsedCsv.sourceRows`); absent falls back to "header on row 1, no blank rows". */
  sourceRows?: number[];
}) {
  // Codex PR #386 review (comment 4373699163): `omitidosDuplicados` combines
  // DuplicadoEnBd (the email already existed, but the row still got
  // ASSOCIATED with the selected Show) with DuplicadoEnArchivo (a true
  // in-file duplicate, never inserted or associated) -- labeling the
  // combined figure "omitidos por duplicado" wrongly told operators every
  // one of these rows was ignored. "duplicados" below makes no omission
  // claim either way; this note only adds the DuplicadoEnBd-specific detail
  // when `result.filas` actually carries it (never fabricated from the
  // aggregate count alone).
  const duplicadosEnBd = result.filas.filter((fila) => fila.resultado === "DuplicadoEnBd").length;

  return (
    <>
      <div className="v2-contact-import__result-header">
        <span
          className={
            result.interrumpido
              ? "v2-contact-import__result-icon v2-contact-import__result-icon--warning"
              : "v2-contact-import__result-icon"
          }
          aria-hidden="true"
        >
          {result.interrumpido ? <WarningIcon /> : <CheckIcon />}
        </span>
        <h3>{result.interrumpido ? "Importación interrumpida" : "Importación terminada"}</h3>
      </div>
      <div className="v2-contact-import__result-figures">
        <div className="v2-contact-import__figure v2-contact-import__figure--positive">
          <span>{result.insertados}</span>
          <span>contactos importados en {showName || "el show elegido"}</span>
        </div>
        <div className="v2-contact-import__figure v2-contact-import__figure--neutral">
          <span>{result.omitidosDuplicados}</span>
          <span>duplicados</span>
        </div>
        <div className="v2-contact-import__figure v2-contact-import__figure--negative">
          <span>{result.rechazados.length}</span>
          <span>{result.rechazados.length === 1 ? "fila rechazada" : "filas rechazadas"}</span>
        </div>
      </div>
      {duplicadosEnBd > 0 && (
        <p className="v2-modal-v2__hint">
          {duplicadosEnBd === 1
            ? "1 de los duplicados ya existía en la base de datos y quedó asociado al show elegido."
            : `${duplicadosEnBd} de los duplicados ya existían en la base de datos y quedaron asociados al show elegido.`}
        </p>
      )}
      {result.rechazados.length > 0 && (
        <ul className="v2-contact-import__rejections" aria-label="Filas rechazadas">
          {result.rechazados.map((rechazo) => (
            <li key={rechazo.fila}>
              {/* Codex round 6 (PR #386): `fila` indexes the submitted records
                  (header excluded, blank rows dropped) -- show the real
                  source-file row an operator must correct. */}
              <strong>Fila {sourceRows?.[rechazo.fila] ?? rechazo.fila + 2}</strong>
              <span>{rechazo.motivo ?? "Motivo no especificado"}</span>
            </li>
          ))}
        </ul>
      )}
      {result.interrumpido && (
        <FeedbackPanel
          variant="error"
          message="La importacion se interrumpio a mitad de camino: las filas ya insertadas quedaron guardadas, el resto quedo sin procesar. Revise el resumen de rechazos y vuelva a intentar con las filas pendientes."
        />
      )}
    </>
  );
}
